/**
 * CardAuthTicker — Global-mount, self-contained live authorization ticker.
 * Polls /api/card-auth/live every ~4s and shows a pulsing neon-emerald bar
 * at the top of the viewport when new events arrive. Fully non-invasive —
 * fixed position, above all content, no layout dependencies. Auto-hides
 * when there are no recent events. Only active for authenticated users.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Radio, X } from "lucide-react";

const POLL_MS = 4000;
const WINDOW_SECONDS = 60;

export default function CardAuthTicker() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);
  const lastSeenIdRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/card-auth/live?seconds=${WINDOW_SECONDS}`);
        if (!alive) return;
        const evts = (data?.events || []).slice(0, 5);
        setEvents(evts);
        if (evts.length > 0 && evts[0].id !== lastSeenIdRef.current) {
          lastSeenIdRef.current = evts[0].id;
          setDismissed(false);
          setPulseKey((k) => k + 1);
        }
      } catch (_) {
        // silent — endpoint may be unauthorized on public pages
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  if (!user || dismissed || events.length === 0) return null;
  const top = events[0];

  return (
    <div
      data-testid="card-auth-ticker"
      className="fixed left-1/2 -translate-x-1/2 z-[9800] top-2 sm:top-3 max-w-[94vw] sm:max-w-[720px] w-full pointer-events-none"
    >
      <div
        key={pulseKey}
        className="card-auth-ticker-bar relative pointer-events-auto rounded-xl overflow-hidden flex items-center gap-3 pl-3 pr-2 py-2"
        style={{
          background: "linear-gradient(90deg, rgba(2,44,34,0.92) 0%, rgba(6,78,59,0.88) 60%, rgba(2,6,23,0.92) 100%)",
          border: "1px solid rgba(16,185,129,0.55)",
          boxShadow: "0 0 0 1px rgba(16,185,129,0.25), 0 20px 40px -20px rgba(16,185,129,0.55)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <span
          data-testid="card-auth-ticker-dot"
          className="w-2 h-2 rounded-full bg-emerald-400 shrink-0"
          style={{ boxShadow: "0 0 10px rgba(16,185,129,0.95)", animation: "cat-dot-pulse 1.1s ease-in-out infinite" }}
        />
        <Radio size={13} className="text-emerald-300 shrink-0" />
        <div className="flex-1 min-w-0 flex items-center gap-2 overflow-hidden">
          <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-300 shrink-0">
            Live Auth
          </span>
          <span
            data-testid="card-auth-ticker-message"
            className="text-white text-sm truncate"
          >
            {top.employee_name} · {top.merchant} · <span className="font-mono">${Number(top.amount).toFixed(2)}</span>
            {top.card_last4 ? <span className="text-slate-400 font-mono"> ····{top.card_last4}</span> : null}
          </span>
          {events.length > 1 && (
            <span
              data-testid="card-auth-ticker-more"
              className="ml-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 shrink-0"
            >
              +{events.length - 1} more
            </span>
          )}
        </div>
        <button
          onClick={() => setDismissed(true)}
          data-testid="card-auth-ticker-dismiss"
          className="p-1 rounded text-slate-400 hover:text-white hover:bg-white/10"
          aria-label="Dismiss ticker"
        >
          <X size={13} />
        </button>
        {/* Pulsing neon under-glow */}
        <span className="card-auth-underglow absolute inset-x-6 -bottom-px h-[2px] rounded-full" />
        {/* Emerald laser sweep — plays once per new event via key remount */}
        <span
          data-testid="card-auth-ticker-laser"
          className="card-auth-laser-sweep pointer-events-none absolute inset-0"
          aria-hidden="true"
        />
      </div>

      <style>{`
        @keyframes cat-dot-pulse {
          0%, 100% { transform: scale(1);   opacity: 1;   box-shadow: 0 0 8px rgba(16,185,129,0.9); }
          50%      { transform: scale(1.4); opacity: 0.6; box-shadow: 0 0 18px rgba(16,185,129,1); }
        }
        @keyframes cat-bar-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.85), 0 20px 40px -20px rgba(16,185,129,0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(16,185,129,0),  0 20px 40px -20px rgba(16,185,129,0.55); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0),     0 20px 40px -20px rgba(16,185,129,0.55); }
        }
        .card-auth-ticker-bar { animation: cat-bar-pulse 1.6s ease-out; }
        .card-auth-underglow {
          background: linear-gradient(90deg, transparent, #10B981, transparent);
          animation: cat-underglow 2.4s ease-in-out infinite;
          opacity: 0.7;
        }
        @keyframes cat-underglow {
          0%, 100% { transform: scaleX(0.85); opacity: 0.4; }
          50%      { transform: scaleX(1.00); opacity: 0.9; }
        }
        /* Emerald laser sweep — a bright vertical light-blade that scans the
           card left→right once when a new authorization arrives, then settles
           back into the glassy obsidian bar. Clip-path keeps the beam inside
           the pill radius so nothing spills over neighboring UI. */
        .card-auth-laser-sweep {
          background: linear-gradient(
            100deg,
            transparent 0%,
            transparent 38%,
            rgba(16,185,129,0.35) 46%,
            rgba(52,211,153,0.85) 50%,
            rgba(16,185,129,0.35) 54%,
            transparent 62%,
            transparent 100%
          );
          filter: blur(0.4px);
          mix-blend-mode: screen;
          -webkit-clip-path: inset(0 round 12px);
          clip-path: inset(0 round 12px);
          transform: translateX(-120%);
          animation: cat-laser-sweep 1.05s cubic-bezier(0.22, 1, 0.36, 1) 1;
        }
        @keyframes cat-laser-sweep {
          0%   { transform: translateX(-120%); opacity: 0;   }
          15%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translateX(120%);  opacity: 0;   }
        }
      `}</style>
    </div>
  );
}
